import packageJson from "../../package.json";

export const APP_BUILD_VERSION = packageJson.version;

const BUILD_STORAGE_KEY = "gympin_app_build";
const RELOAD_GUARD_KEY = "gympin_sw_reloading";

/** Keys that must survive a one-time version migration (redux-persist + push). */
const PRESERVED_STORAGE_KEYS = [
    "persist:gympin-pwa-auth",
    "persist:gympin-pwa-invocie",
    "persist:gympin-pwa-setting",
    "push_endpoint",
    "push_userId",
    BUILD_STORAGE_KEY,
];

/**
 * Lightweight full-screen notice before a forced reload.
 * No React — must work even when the old bundle is broken.
 */
export function showUpdatingOverlay(message = "در حال به‌روزرسانی…") {
    if (typeof document === "undefined") return;
    if (document.getElementById("gympin-updating-overlay")) return;

    const overlay = document.createElement("div");
    overlay.id = "gympin-updating-overlay";
    overlay.setAttribute("dir", "rtl");
    overlay.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:2147483647",
        "display:flex",
        "flex-direction:column",
        "align-items:center",
        "justify-content:center",
        "gap:16px",
        "background:#0f172a",
        "color:#f8fafc",
        "font-family:Tahoma,IRANSans,sans-serif",
        "font-size:16px",
        "text-align:center",
        "padding:24px",
    ].join(";");

    const spinner = document.createElement("div");
    spinner.style.cssText = [
        "width:36px",
        "height:36px",
        "border:3px solid rgba(248,250,252,0.25)",
        "border-top-color:#38bdf8",
        "border-radius:50%",
        "animation:gympin-spin 0.8s linear infinite",
    ].join(";");

    if (!document.getElementById("gympin-updating-style")) {
        const style = document.createElement("style");
        style.id = "gympin-updating-style";
        style.textContent = "@keyframes gympin-spin{to{transform:rotate(360deg)}}";
        document.head.appendChild(style);
    }

    const text = document.createElement("div");
    text.textContent = message;

    overlay.appendChild(spinner);
    overlay.appendChild(text);
    document.body.appendChild(overlay);
}

export function clearReloadGuard() {
    try {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (_) {
        /* ignore */
    }
}

export function reloadOnceWithOverlay(message) {
    try {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") {
            return;
        }
        sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } catch (_) {
        /* ignore */
    }
    showUpdatingOverlay(message);
    window.location.reload();
}

function snapshotPreservedLocalStorage() {
    const saved = {};
    PRESERVED_STORAGE_KEYS.forEach((key) => {
        try {
            const value = localStorage.getItem(key);
            if (value !== null) {
                saved[key] = value;
            }
        } catch (_) {
            /* ignore */
        }
    });
    return saved;
}

function restorePreservedLocalStorage(saved) {
    Object.keys(saved).forEach((key) => {
        try {
            localStorage.setItem(key, saved[key]);
        } catch (_) {
            /* ignore */
        }
    });
}

async function clearAllCacheStorage() {
    if (!("caches" in window)) return;
    try {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (_) {
        /* ignore */
    }
}

/**
 * One-time migration when package.json version changes (or is missing for legacy installs).
 * Clears Cache Storage + non-preserved local/session storage, then reloads once.
 * @returns {Promise<boolean>} true if a reload was triggered
 */
export async function runAppBuildMigration() {
    if (typeof window === "undefined") {
        return false;
    }

    // After a guarded reload, drop the flag so future SW updates can reload again.
    try {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") {
            sessionStorage.removeItem(RELOAD_GUARD_KEY);
        }
    } catch (_) {
        /* ignore */
    }

    let storedBuild = null;
    try {
        storedBuild = localStorage.getItem(BUILD_STORAGE_KEY);
    } catch (_) {
        storedBuild = null;
    }

    if (storedBuild === APP_BUILD_VERSION) {
        return false;
    }

    showUpdatingOverlay("در حال به‌روزرسانی…");

    const preserved = snapshotPreservedLocalStorage();

    await clearAllCacheStorage();

    try {
        localStorage.clear();
    } catch (_) {
        /* ignore */
    }
    restorePreservedLocalStorage(preserved);

    try {
        localStorage.setItem(BUILD_STORAGE_KEY, APP_BUILD_VERSION);
    } catch (_) {
        /* ignore */
    }

    try {
        sessionStorage.clear();
        sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
    } catch (_) {
        /* ignore */
    }

    window.location.reload();
    return true;
}
